import apiClient from '@/shared/api/client'
import type {
  ServiceMaster, ApprovalMatrix, PurchaseRequisition,
  RFQ, RFQVendor, Quotation, ComparativeStatement,
  WorkOrder, ShipmentAdvice, ServiceEntry,
  RejectionNote, VendorPerformance, PurchaseInvoice,
} from '@/shared/types/procurement'

// ── Service Masters ───────────────────────────────────────────────────────────

export const serviceMastersApi = {
  list: async (params?: Record<string, unknown>): Promise<ServiceMaster[]> => {
    const res = await apiClient.get<ServiceMaster[]>('/purchase/service-masters', { params })
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<ServiceMaster> => {
    const res = await apiClient.post<ServiceMaster>('/purchase/service-masters', data)
    return res.data
  },
  update: async (id: string, data: Record<string, unknown>): Promise<ServiceMaster> => {
    const res = await apiClient.patch<ServiceMaster>(`/purchase/service-masters/${id}`, data)
    return res.data
  },
}

// ── Approval Matrix ───────────────────────────────────────────────────────────

export const approvalMatrixApi = {
  list: async (params?: Record<string, unknown>): Promise<ApprovalMatrix[]> => {
    const res = await apiClient.get<ApprovalMatrix[]>('/purchase/approval-matrix', { params })
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<ApprovalMatrix> => {
    const res = await apiClient.post<ApprovalMatrix>('/purchase/approval-matrix', data)
    return res.data
  },
  update: async (id: string, data: Record<string, unknown>): Promise<ApprovalMatrix> => {
    const res = await apiClient.patch<ApprovalMatrix>(`/purchase/approval-matrix/${id}`, data)
    return res.data
  },
}

// ── Purchase Requisitions ─────────────────────────────────────────────────────

export const purchaseRequisitionsApi = {
  list: async (params?: Record<string, unknown>): Promise<PurchaseRequisition[]> => {
    const res = await apiClient.get<PurchaseRequisition[]>('/purchase/requisitions', { params })
    return res.data
  },
  get: async (id: string): Promise<PurchaseRequisition> => {
    const res = await apiClient.get<PurchaseRequisition>(`/purchase/requisitions/${id}`)
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<PurchaseRequisition> => {
    const res = await apiClient.post<PurchaseRequisition>('/purchase/requisitions', data)
    return res.data
  },
  update: async (id: string, data: Record<string, unknown>): Promise<PurchaseRequisition> => {
    const res = await apiClient.patch<PurchaseRequisition>(`/purchase/requisitions/${id}`, data)
    return res.data
  },
  submit: async (id: string): Promise<PurchaseRequisition> => {
    const res = await apiClient.post<PurchaseRequisition>(`/purchase/requisitions/${id}/submit`)
    return res.data
  },
  approve: async (id: string, data: Record<string, unknown>): Promise<PurchaseRequisition> => {
    const res = await apiClient.post<PurchaseRequisition>(`/purchase/requisitions/${id}/approve`, data)
    return res.data
  },
}

// ── RFQ ───────────────────────────────────────────────────────────────────────

export const rfqApi = {
  list: async (params?: Record<string, unknown>): Promise<RFQ[]> => {
    const res = await apiClient.get<RFQ[]>('/purchase/rfqs', { params })
    return res.data
  },
  get: async (id: string): Promise<RFQ> => {
    const res = await apiClient.get<RFQ>(`/purchase/rfqs/${id}`)
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<RFQ> => {
    const res = await apiClient.post<RFQ>('/purchase/rfqs', data)
    return res.data
  },
  close: async (id: string): Promise<RFQ> => {
    const res = await apiClient.post<RFQ>(`/purchase/rfqs/${id}/close`)
    return res.data
  },
  addVendor: async (rfqId: string, data: Record<string, unknown>): Promise<RFQVendor> => {
    const res = await apiClient.post<RFQVendor>(`/purchase/rfqs/${rfqId}/vendors`, data)
    return res.data
  },
  listVendors: async (rfqId: string): Promise<RFQVendor[]> => {
    const res = await apiClient.get<RFQVendor[]>(`/purchase/rfqs/${rfqId}/vendors`)
    return res.data
  },
  markVendorResponded: async (rfqVendorId: string): Promise<RFQVendor> => {
    const res = await apiClient.post<RFQVendor>(`/purchase/rfq-vendors/${rfqVendorId}/respond`)
    return res.data
  },
}

// ── Quotations ────────────────────────────────────────────────────────────────

export const quotationsApi = {
  list: async (params?: Record<string, unknown>): Promise<Quotation[]> => {
    const res = await apiClient.get<Quotation[]>('/purchase/quotations', { params })
    return res.data
  },
  get: async (id: string): Promise<Quotation> => {
    const res = await apiClient.get<Quotation>(`/purchase/quotations/${id}`)
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<Quotation> => {
    const res = await apiClient.post<Quotation>('/purchase/quotations', data)
    return res.data
  },
}

// ── Comparative Statements ────────────────────────────────────────────────────

export const comparativeStatementsApi = {
  list: async (params?: Record<string, unknown>): Promise<ComparativeStatement[]> => {
    const res = await apiClient.get<ComparativeStatement[]>('/purchase/comparative-statements', { params })
    return res.data
  },
  get: async (id: string): Promise<ComparativeStatement> => {
    const res = await apiClient.get<ComparativeStatement>(`/purchase/comparative-statements/${id}`)
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<ComparativeStatement> => {
    const res = await apiClient.post<ComparativeStatement>('/purchase/comparative-statements', data)
    return res.data
  },
  approve: async (id: string, data: Record<string, unknown>): Promise<ComparativeStatement> => {
    const res = await apiClient.post<ComparativeStatement>(`/purchase/comparative-statements/${id}/approve`, data)
    return res.data
  },
}

// ── Work Orders ───────────────────────────────────────────────────────────────

export const workOrdersApi = {
  list: async (params?: Record<string, unknown>): Promise<WorkOrder[]> => {
    const res = await apiClient.get<WorkOrder[]>('/purchase/work-orders', { params })
    return res.data
  },
  get: async (id: string): Promise<WorkOrder> => {
    const res = await apiClient.get<WorkOrder>(`/purchase/work-orders/${id}`)
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<WorkOrder> => {
    const res = await apiClient.post<WorkOrder>('/purchase/work-orders', data)
    return res.data
  },
  update: async (id: string, data: Record<string, unknown>): Promise<WorkOrder> => {
    const res = await apiClient.patch<WorkOrder>(`/purchase/work-orders/${id}`, data)
    return res.data
  },
  approve: async (id: string): Promise<WorkOrder> => {
    const res = await apiClient.post<WorkOrder>(`/purchase/work-orders/${id}/approve`)
    return res.data
  },
}

// ── Shipment Advices ──────────────────────────────────────────────────────────

export const shipmentAdvicesApi = {
  list: async (params?: Record<string, unknown>): Promise<ShipmentAdvice[]> => {
    const res = await apiClient.get<ShipmentAdvice[]>('/purchase/shipment-advices', { params })
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<ShipmentAdvice> => {
    const res = await apiClient.post<ShipmentAdvice>('/purchase/shipment-advices', data)
    return res.data
  },
  update: async (id: string, data: Record<string, unknown>): Promise<ShipmentAdvice> => {
    const res = await apiClient.patch<ShipmentAdvice>(`/purchase/shipment-advices/${id}`, data)
    return res.data
  },
}

// ── Service Entries ───────────────────────────────────────────────────────────

export const serviceEntriesApi = {
  list: async (params?: Record<string, unknown>): Promise<ServiceEntry[]> => {
    const res = await apiClient.get<ServiceEntry[]>('/purchase/service-entries', { params })
    return res.data
  },
  get: async (id: string): Promise<ServiceEntry> => {
    const res = await apiClient.get<ServiceEntry>(`/purchase/service-entries/${id}`)
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<ServiceEntry> => {
    const res = await apiClient.post<ServiceEntry>('/purchase/service-entries', data)
    return res.data
  },
  approve: async (id: string, data: Record<string, unknown>): Promise<ServiceEntry> => {
    const res = await apiClient.post<ServiceEntry>(`/purchase/service-entries/${id}/approve`, data)
    return res.data
  },
}

// ── Rejection Notes ───────────────────────────────────────────────────────────

export const rejectionNotesApi = {
  list: async (params?: Record<string, unknown>): Promise<RejectionNote[]> => {
    const res = await apiClient.get<RejectionNote[]>('/purchase/rejection-notes', { params })
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<RejectionNote> => {
    const res = await apiClient.post<RejectionNote>('/purchase/rejection-notes', data)
    return res.data
  },
  update: async (id: string, data: Record<string, unknown>): Promise<RejectionNote> => {
    const res = await apiClient.patch<RejectionNote>(`/purchase/rejection-notes/${id}`, data)
    return res.data
  },
}

// ── Vendor Performance ────────────────────────────────────────────────────────

export const vendorPerformanceApi = {
  list: async (params?: Record<string, unknown>): Promise<VendorPerformance[]> => {
    const res = await apiClient.get<VendorPerformance[]>('/purchase/vendor-performance', { params })
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<VendorPerformance> => {
    const res = await apiClient.post<VendorPerformance>('/purchase/vendor-performance', data)
    return res.data
  },
  recalculate: async (params: Record<string, unknown>): Promise<VendorPerformance> => {
    const res = await apiClient.post<VendorPerformance>('/purchase/vendor-performance/recalculate', null, { params })
    return res.data
  },
}

// ── Purchase Invoices ─────────────────────────────────────────────────────────

export const purchaseInvoicesApi = {
  list: async (params?: Record<string, unknown>): Promise<PurchaseInvoice[]> => {
    const res = await apiClient.get<PurchaseInvoice[]>('/purchase/invoices', { params })
    return res.data
  },
  get: async (id: string): Promise<PurchaseInvoice> => {
    const res = await apiClient.get<PurchaseInvoice>(`/purchase/invoices/${id}`)
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<PurchaseInvoice> => {
    const res = await apiClient.post<PurchaseInvoice>('/purchase/invoices', data)
    return res.data
  },
  match: async (id: string): Promise<{ invoice: PurchaseInvoice; status: string; reason: string; open_rejections: string[] }> => {
    const res = await apiClient.post(`/purchase/invoices/${id}/match`)
    return res.data
  },
  approve: async (id: string): Promise<PurchaseInvoice> => {
    const res = await apiClient.post<PurchaseInvoice>(`/purchase/invoices/${id}/approve`)
    return res.data
  },
  markPaid: async (id: string, paymentRef?: string): Promise<PurchaseInvoice> => {
    const params = paymentRef ? { payment_ref: paymentRef } : undefined
    const res = await apiClient.post<PurchaseInvoice>(`/purchase/invoices/${id}/mark-paid`, null, { params })
    return res.data
  },
}
